// =============================================================================
// features/documents/hooks.ts — TanStack Query sobre features/documents/api.ts
// (F46-S08).
//
// Query keys com escopo explícito (nota do slot):
//   ['documents', 'list', 'lead', leadId]
//   ['documents', 'list', 'context', scope, leadId, contextId]
//   ['documents', 'types']
//
// Cada lista usa `useQuery` — nunca `useInfiniteQuery` na mesma chave (nota
// do slot: já derrubou o observer do live chat). Os endpoints consumidos aqui
// (`/api/leads/:id/documents` e `/api/documents` filtrado por lead) devolvem
// no máximo dezenas de itens — paginação real fica para quando (se) isso
// deixar de ser verdade.
//
// Upload: `runDocumentUploadStep` é a função pura por trás de
// `useDocumentUpload` — orquestra downscale → signed-url → PUT → attach com
// dependências injetadas, testável sem React (o projeto não tem
// @testing-library/react — ver __tests__/hooks.test.ts).
// =============================================================================

import type { Document, DocumentType, SignedUrlRequest } from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuth } from '../../lib/auth-store';
import { formatOrgDate } from '../../lib/orgDateTime';

import {
  attachDocument,
  deleteDocument,
  downloadDocumentBlob,
  fetchDocuments,
  fetchDocumentTypesCatalog,
  fetchLeadAnalysesForSelector,
  fetchLeadContractsForSelector,
  fetchLeadDocuments,
  triggerBlobDownload,
  uploadToSignedUrl,
  DocumentDownloadForbiddenError,
  LeadLinkOptionsForbiddenError,
  createDocumentSignedUrl,
  type ContractOption,
  type LeadAnalysisOption,
} from './api';
import { downscaleImage } from './lib/downscaleImage';

// ---------------------------------------------------------------------------
// Flag + RBAC
// ---------------------------------------------------------------------------

export const DOCUMENTS_FLAG_KEY = 'documents.enabled';

export interface DocumentsGate {
  /** true quando a flag está ligada E o ator tem `documents:read`. */
  canRead: boolean;
  canWrite: boolean;
  canDownload: boolean;
  canDelete: boolean;
  /** true enquanto a flag ainda não respondeu — evita "flash" de conteúdo. */
  isLoading: boolean;
}

/**
 * Gate único do módulo. `documents:download` é DELIBERADAMENTE separada de
 * `documents:read` (DPIA F46-S16) — um usuário pode legitimamente listar e
 * não poder baixar. Nunca inferir uma permissão a partir da outra.
 */
export function useDocumentsGate(): DocumentsGate {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading } = useFeatureFlag(DOCUMENTS_FLAG_KEY);

  const canRead = flagEnabled && hasPermission('documents:read');

  return {
    canRead,
    canWrite: canRead && hasPermission('documents:write'),
    canDownload: canRead && hasPermission('documents:download'),
    canDelete: canRead && hasPermission('documents:delete'),
    isLoading,
  };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const documentsQueryKeys = {
  all: ['documents'] as const,
  types: () => [...documentsQueryKeys.all, 'types'] as const,
  lead: (leadId: string) => [...documentsQueryKeys.all, 'list', 'lead', leadId] as const,
  context: (scope: 'analysis' | 'contract', leadId: string, contextId: string) =>
    [...documentsQueryKeys.all, 'list', 'context', scope, leadId, contextId] as const,
};

// ---------------------------------------------------------------------------
// Listagem — scope='lead'
// ---------------------------------------------------------------------------

export function useLeadDocuments(leadId: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: documentsQueryKeys.lead(leadId),
    queryFn: () => fetchLeadDocuments(leadId),
    enabled: (opts.enabled ?? true) && Boolean(leadId),
  });
}

// ---------------------------------------------------------------------------
// Listagem — scope='analysis'|'contract'
// ---------------------------------------------------------------------------

export interface UseContextDocumentsArgs {
  leadId: string;
  scope: 'analysis' | 'contract';
  contextId: string;
  enabled?: boolean;
}

export function useContextDocuments({
  leadId,
  scope,
  contextId,
  enabled,
}: UseContextDocumentsArgs) {
  return useQuery({
    queryKey: documentsQueryKeys.context(scope, leadId, contextId),
    queryFn: () =>
      fetchDocuments({
        leadId,
        ...(scope === 'analysis' ? { analysisId: contextId } : { contractId: contextId }),
      }),
    enabled: (enabled ?? true) && Boolean(leadId) && Boolean(contextId),
  });
}

// ---------------------------------------------------------------------------
// Catálogo de tipos (para o seletor do upload) — ver nota de arquitetura em api.ts
// ---------------------------------------------------------------------------

export function useDocumentTypesCatalog() {
  return useQuery({
    queryKey: documentsQueryKeys.types(),
    queryFn: fetchDocumentTypesCatalog,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Seletor de vínculo (análise/contrato do lead) — upload a partir da ficha
// (F46-S20)
// ---------------------------------------------------------------------------
//
// Regras do slot: no máximo um contexto (o CHECK do banco recusa dois — a UI
// nunca deixa o usuário chegar lá); opcional de verdade (seguir sem vínculo é
// o padrão); só análises/contratos DO PRÓPRIO lead (sem busca livre); lead
// sem nenhum dos dois → o seletor não aparece (campo vazio que nunca
// preenche é ruído). O valor do `<select>` codifica no máximo um tipo por vez
// (`analysis:<id>` | `contract:<id>`), o que já torna "escolher os dois"
// estruturalmente impossível — não há como um único value representar dois
// contextos.

const ANALYSIS_STATUS_LABEL: Record<string, string> = {
  em_analise: 'Em análise',
  pendente: 'Pendente',
  aprovado: 'Aprovado',
  recusado: 'Recusado',
  cancelado: 'Cancelado',
};

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  signed: 'Assinado',
  active: 'Ativo',
  settled: 'Quitado',
  defaulted: 'Inadimplente',
  cancelled: 'Cancelado',
};

export interface LeadLinkOption {
  /** `analysis:<id>` | `contract:<id>` — nunca os dois ao mesmo tempo. */
  value: string;
  label: string;
}

export function formatAnalysisLinkOption(analysis: LeadAnalysisOption): LeadLinkOption {
  const statusLabel = ANALYSIS_STATUS_LABEL[analysis.status] ?? analysis.status;
  return {
    value: `analysis:${analysis.id}`,
    label: `Análise — ${statusLabel} (${formatOrgDate(analysis.created_at)})`,
  };
}

export function formatContractLinkOption(contract: ContractOption): LeadLinkOption {
  const statusLabel = CONTRACT_STATUS_LABEL[contract.status] ?? contract.status;
  return {
    value: `contract:${contract.id}`,
    label: `Contrato ${contract.contract_reference} — ${statusLabel}`,
  };
}

export interface ParsedLinkValue {
  scope: 'analysis' | 'contract';
  contextId: string;
}

/** Decodifica o value do `<select>` de vínculo. `''` (nenhum vínculo) e
 * valores desconhecidos devolvem `null` — o chamador cai de volta no scope
 * original (`'lead'`). */
export function parseLinkValue(value: string): ParsedLinkValue | null {
  if (value.startsWith('analysis:')) {
    const contextId = value.slice('analysis:'.length);
    return contextId ? { scope: 'analysis', contextId } : null;
  }
  if (value.startsWith('contract:')) {
    const contextId = value.slice('contract:'.length);
    return contextId ? { scope: 'contract', contextId } : null;
  }
  return null;
}

/** Decide se o bloco do seletor de vínculo deve renderizar algo. Enquanto
 * carrega, nada aparece (evita flicker). Sem opções e sem erro de permissão,
 * o bloco fica ausente — não um `<select>` vazio (regra do slot). Com erro
 * de permissão (403 em análises OU contratos), o bloco aparece mesmo sem
 * opções para avisar — nunca engolir em silêncio. */
export function shouldShowLinkBlock(state: {
  loading: boolean;
  optionsCount: number;
  forbidden: boolean;
}): boolean {
  if (state.loading) return false;
  return state.optionsCount > 0 || state.forbidden;
}

export interface UseLeadLinkOptionsResult {
  options: LeadLinkOption[];
  isLoading: boolean;
  /** true quando falta permissão para ler análises OU contratos do lead. */
  isForbidden: boolean;
}

/**
 * Opções de vínculo (análise/contrato) do lead, para o upload a partir da
 * ficha. `enabled=false` desliga as duas queries (usado quando `scope !==
 * 'lead'` ou o diálogo está fechado — nada de fetch fora de contexto).
 */
export function useLeadLinkOptions(leadId: string, enabled: boolean): UseLeadLinkOptionsResult {
  const analysesQuery = useQuery({
    queryKey: [...documentsQueryKeys.all, 'lead-link-analyses', leadId],
    queryFn: () => fetchLeadAnalysesForSelector(leadId),
    enabled: enabled && Boolean(leadId),
    retry: false,
  });
  const contractsQuery = useQuery({
    queryKey: [...documentsQueryKeys.all, 'lead-link-contracts', leadId],
    queryFn: () => fetchLeadContractsForSelector(leadId),
    enabled: enabled && Boolean(leadId),
    retry: false,
  });

  const options: LeadLinkOption[] = [
    ...(analysesQuery.data ?? []).map(formatAnalysisLinkOption),
    ...(contractsQuery.data ?? []).map(formatContractLinkOption),
  ];

  const isForbidden =
    analysesQuery.error instanceof LeadLinkOptionsForbiddenError ||
    contractsQuery.error instanceof LeadLinkOptionsForbiddenError;

  return {
    options,
    isLoading: enabled && (analysesQuery.isLoading || contractsQuery.isLoading),
    isForbidden,
  };
}

// ---------------------------------------------------------------------------
// Invalidação — usada por upload/delete
// ---------------------------------------------------------------------------

function useInvalidateDocuments() {
  const queryClient = useQueryClient();
  return React.useCallback(
    () => queryClient.invalidateQueries({ queryKey: documentsQueryKeys.all }),
    [queryClient],
  );
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export function useDeleteDocument() {
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
    onSuccess: () => {
      void invalidate();
    },
  });
}

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

export interface UseDownloadDocumentResult {
  download: (args: { documentId: string; filename: string }) => void;
  isDownloading: boolean;
  /** true quando o último erro foi 403 (sem `documents:download`) — a UI
   * mostra "sem permissão", não um erro genérico. */
  isForbidden: boolean;
  error: Error | null;
  reset: () => void;
}

export function useDownloadDocument(): UseDownloadDocumentResult {
  const mutation = useMutation({
    mutationFn: async ({ documentId, filename }: { documentId: string; filename: string }) => {
      const { blob } = await downloadDocumentBlob(documentId);
      triggerBlobDownload(blob, filename);
    },
  });

  return {
    download: mutation.mutate,
    isDownloading: mutation.isPending,
    isForbidden: mutation.error instanceof DocumentDownloadForbiddenError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ---------------------------------------------------------------------------
// Upload — passo a passo puro (testável sem React)
// ---------------------------------------------------------------------------

export interface UploadFileContext {
  leadId: string;
  documentTypeId: string;
  scope: 'lead' | 'analysis' | 'contract';
  contextId?: string | undefined;
}

export interface UploadStepDeps {
  downscale: (file: File) => Promise<File>;
  signUrl: (body: SignedUrlRequest) => Promise<{ uploadUrl: string; key: string }>;
  putFile: (args: {
    uploadUrl: string;
    file: File;
    mime: string;
    onProgress?: (percent: number) => void;
  }) => Promise<void>;
  attach: (body: Parameters<typeof attachDocument>[0], idempotencyKey: string) => Promise<Document>;
  makeIdempotencyKey: () => string;
}

export const defaultUploadStepDeps: UploadStepDeps = {
  downscale: downscaleImage,
  signUrl: createDocumentSignedUrl,
  putFile: uploadToSignedUrl,
  attach: attachDocument,
  makeIdempotencyKey: () => crypto.randomUUID(),
};

/**
 * Orquestra o upload de UM arquivo (downscale → signed-url → PUT → attach).
 *
 * Contrato de falha (DoD: "falha em qualquer passo não deixa card fantasma"):
 * o documento só existe no servidor (e portanto só aparece na lista após
 * invalidação) depois que `attach` resolve. Se `signUrl` ou `putFile`
 * falharem, `attach` NUNCA é chamado — não há card para "sobrar".
 */
export async function runDocumentUploadStep(
  file: File,
  ctx: UploadFileContext,
  onProgress: (percent: number) => void,
  deps: UploadStepDeps = defaultUploadStepDeps,
): Promise<Document> {
  const processed = await deps.downscale(file);

  const { uploadUrl, key } = await deps.signUrl({
    leadId: ctx.leadId,
    documentTypeId: ctx.documentTypeId,
    mime: processed.type || file.type,
    sizeBytes: processed.size,
  });

  await deps.putFile({
    uploadUrl,
    file: processed,
    mime: processed.type || file.type,
    onProgress,
  });

  const idempotencyKey = deps.makeIdempotencyKey();

  return deps.attach(
    {
      key,
      leadId: ctx.leadId,
      documentTypeId: ctx.documentTypeId,
      mime: processed.type || file.type,
      sizeBytes: processed.size,
      originalFilename: file.name,
      ...(ctx.scope === 'analysis' && ctx.contextId ? { analysisId: ctx.contextId } : {}),
      ...(ctx.scope === 'contract' && ctx.contextId ? { contractId: ctx.contextId } : {}),
    },
    idempotencyKey,
  );
}

// ---------------------------------------------------------------------------
// Upload — estado por arquivo (hook de UI)
// ---------------------------------------------------------------------------

export type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'error';

export interface UploadItem {
  id: string;
  file: File;
  status: UploadItemStatus;
  progress: number;
  error?: string | undefined;
}

export interface UseDocumentUploadResult {
  items: UploadItem[];
  addFiles: (files: File[]) => void;
  removeItem: (id: string) => void;
  reset: () => void;
  /** Dispara o upload de todos os itens `pending`/`error` para o `documentTypeId` dado. */
  startUpload: (documentTypeId: string) => Promise<void>;
  isUploading: boolean;
  /** true quando todos os itens terminaram com sucesso. */
  isAllDone: boolean;
}

export function useDocumentUpload(ctx: {
  leadId: string;
  scope: 'lead' | 'analysis' | 'contract';
  contextId?: string | undefined;
}): UseDocumentUploadResult {
  const [items, setItems] = React.useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const invalidate = useInvalidateDocuments();

  const addFiles = React.useCallback((files: File[]) => {
    setItems((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: 'pending' as const,
        progress: 0,
      })),
    ]);
  }, []);

  const removeItem = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const reset = React.useCallback(() => {
    setItems([]);
    setIsUploading(false);
  }, []);

  const startUpload = React.useCallback(
    async (documentTypeId: string): Promise<void> => {
      setIsUploading(true);
      try {
        const pending = items.filter((it) => it.status !== 'done');

        for (const item of pending) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id
                ? { ...it, status: 'uploading', progress: 0, error: undefined }
                : it,
            ),
          );

          try {
            await runDocumentUploadStep(
              item.file,
              { leadId: ctx.leadId, documentTypeId, scope: ctx.scope, contextId: ctx.contextId },
              (percent) => {
                setItems((prev) =>
                  prev.map((it) => (it.id === item.id ? { ...it, progress: percent } : it)),
                );
              },
            );
            setItems((prev) =>
              prev.map((it) => (it.id === item.id ? { ...it, status: 'done', progress: 100 } : it)),
            );
            void invalidate();
          } catch (err) {
            setItems((prev) =>
              prev.map((it) =>
                it.id === item.id
                  ? {
                      ...it,
                      status: 'error',
                      error: err instanceof Error ? err.message : 'Falha no upload.',
                    }
                  : it,
              ),
            );
          }
        }
      } finally {
        setIsUploading(false);
      }
    },
    [items, ctx.leadId, ctx.scope, ctx.contextId, invalidate],
  );

  const isAllDone = items.length > 0 && items.every((it) => it.status === 'done');

  return { items, addFiles, removeItem, reset, startUpload, isUploading, isAllDone };
}

export type { DocumentType };
